import { useRef, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Text } from '@factorialco/f0-react'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import {
  ShiftManagementSelectableShiftsRowsDocument,
  type PlanningBalancesByPeriodsQuery,
  type PlanningBalancesQuery,
} from 'generated/resources/graphql'
import moment from 'moment'

import { i18nDateToString, type FormatTypes } from 'modules/core/lib/dates'
import { TimeGridTable } from 'modules/shift_management/components/ShiftManagement/TimeGridTable/index'

import type { ShiftsRow as TShiftsRow, View } from 'modules/shift_management/lib/types'
import { resolveDimension } from 'modules/shift_management/utils/TimeGridTable/dimensions'
import { formatHeader } from 'modules/shift_management/utils/TimeGridTable/buildHeader'
import {
  buildTimeSlots,
  computeExtraHoursForTimeRange,
} from 'modules/shift_management/utils/TimeGridTable/slots'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { useLocalStorage } from 'usehooks-ts'
import { FirstCell } from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/Row/FirstCell'
import type {
  GridSelection,
  TimeGranularity,
  TimeSlot,
} from 'modules/shift_management/components/ShiftManagement/TimeGridTable/types'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useBuildEvents } from 'modules/shift_management/hooks/gridTable/useBuildEvents'
import { useBuildTimeOffSummary } from 'modules/shift_management/hooks/gridTable/useBuildTimeOffSummary'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { resolveCard } from 'modules/shift_management/utils/TimeGridTable/resolveCard'
import i18n from 'modules/core/lib/i18n'
import SortEmployeesButton from 'modules/shift_management/components/ShiftManagement/Content/Body/ShiftManagementTimeGrid/SortEmployeesButton'
import { SortWorkAreasButton } from 'modules/shift_management/components/ShiftManagement/Content/Body/ShiftManagementTimeGrid/SortWorkAreasButton'
import useGetTimeOffRow from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/TimeOffRow/useGetTimeOffRow'
import TimeOffSidepanel from 'modules/shift_management/components/ShiftManagement/TimeOffSidepanel'
import { useGridClipboardActions } from 'modules/shift_management/hooks/gridTable/useGridClipboardActions'
import { useGridDragAndDropAdapter } from 'modules/shift_management/hooks/gridTable/useGridDragAndDropAdapter'
import {
  useSelectionDispatch,
  useSelectionState,
} from 'modules/shift_management/hooks/useSelection'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useDeleteSelectionFlow } from 'modules/shift_management/hooks/useDeleteSelectionFlow'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import { usePermissions } from 'modules/shift_management/hooks/usePermissions'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { LOGIC_OPERATOR } from 'modules/shift_management/hooks/reducers/filter'
import { useOrderOptions } from 'modules/shift_management/hooks/useOrderEmployees'
import { useCoverageColumns } from 'modules/shift_management/components/ShiftManagement/Content/Body/ShiftManagementTimeGrid/useCoverageColumns'
import CoverageSection from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/CoverageSection/index'
import useMoveShift from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/utils/useMoveShift'
import useFetchDefaultShifts from 'modules/shift_management/components/ShiftManagement/MainPage/useFetchDefaultShifts'
import QuickActionsPopover from 'modules/shift_management/components/ShiftManagement/QuickActionsPopover'
import { fromCellKey } from 'modules/shift_management/utils/TimeGridTable/cellCoordinates'
import type { DaysByRow } from 'modules/shift_management/hooks/reducers/selection'
import type {
  ShiftGridEvent,
  RestDayGridEvent,
  ExtendedGridEventAppearance,
} from 'modules/shift_management/utils/TimeGridTable/transformEvents'
import type { Shift, RestDay, UnassignedShift } from 'modules/shift_management/lib/types'
import { trackAddShiftStartEvent } from 'modules/shift_management/lib/trackEvents'
import ShiftsRow, {
  ShiftsRowType,
  type TShiftsRow as TShiftsRowGraphQL,
} from 'modules/shift_management/store/internal/models/ShiftsRow'
import {
  COVERAGE_SECTION_HEADER,
  HEADER_OFFSET,
} from 'modules/shift_management/lib/consts/ShiftManagementCoverage'
interface BuildDaysByRowDeps {
  events: ExtendedGridEventAppearance[]
  entities: { id: string; selectable?: boolean }[]
  dateFormat: FormatTypes
  isSelectableDayForGridSelection: (rowKey: string, date: moment.Moment) => boolean
}

type GhostCell = { entityId: string; slotIndex: number }

const buildDaysByRow = (
  selection: Exclude<GridSelection, { kind: 'none' }>,
  gridSlots: TimeSlot[],
  { events, entities, dateFormat, isSelectableDayForGridSelection }: BuildDaysByRowDeps
): DaysByRow => {
  const daysByRow: DaysByRow = {}

  if (selection.kind === 'all') {
    const slotDates = gridSlots.map((slot) => moment(slot.start))
    for (const entity of entities) {
      if (entity.id === TIME_OFF_ENTITY_ID) continue
      if (entity.selectable === false) continue
      const selectedDays = slotDates
        .filter((date) => isSelectableDayForGridSelection(entity.id, date))
        .map((date) => i18nDateToString(date, dateFormat))

      if (selectedDays.length > 0) {
        daysByRow[entity.id] = selectedDays
      }
    }
  } else if (selection.kind === 'event') {
    // Multi-event selections (Cmd/Ctrl+click) are translated into global/header
    // selection so bulk actions (Add/Delete/Edit) work from the top bar.
    for (const key of selection.keys) {
      const separatorIndex = key.indexOf(':')
      if (separatorIndex === -1) continue
      const entityId = key.substring(0, separatorIndex)
      const eventId = key.substring(separatorIndex + 1)

      if (entityId === TIME_OFF_ENTITY_ID) continue

      const event = events.find((e) => e.id === eventId && e.entityId === entityId)
      if (!event) continue

      if (event.type !== 'shift' && event.type !== 'rest_day') continue

      const date = moment(event.start)
      if (!isSelectableDayForGridSelection(entityId, date)) continue

      const dateStr = i18nDateToString(date, dateFormat)
      const existing = daysByRow[entityId] ?? []
      if (!existing.includes(dateStr)) {
        existing.push(dateStr)
      }
      daysByRow[entityId] = existing
    }
  } else if (selection.kind === 'cells') {
    for (const key of selection.keys) {
      const { entityId, slotIndex } = fromCellKey(key)
      if (entityId === TIME_OFF_ENTITY_ID) continue
      const slot = gridSlots[slotIndex]
      if (!slot) continue
      const date = moment(slot.start)
      if (!isSelectableDayForGridSelection(entityId, date)) continue
      const dateStr = i18nDateToString(date, dateFormat)
      const existing = daysByRow[entityId] ?? []
      existing.push(dateStr)
      daysByRow[entityId] = existing
    }
  } else if (selection.kind === 'row') {
    if (selection.rowId === TIME_OFF_ENTITY_ID) return daysByRow
    const selectedDays = gridSlots
      .map((slot) => moment(slot.start))
      .filter((date) => isSelectableDayForGridSelection(selection.rowId, date))
      .map((date) => i18nDateToString(date, dateFormat))

    if (selectedDays.length > 0) {
      daysByRow[selection.rowId] = selectedDays
    }
  } else if (selection.kind === 'rowRange') {
    const selectedEntities = entities.filter((entity) => selection.keys.has(entity.id))

    for (const entity of selectedEntities) {
      if (entity.id === TIME_OFF_ENTITY_ID || entity.selectable === false) continue

      const selectedDays = gridSlots
        .map((slot) => moment(slot.start))
        .filter((date) => isSelectableDayForGridSelection(entity.id, date))
        .map((date) => i18nDateToString(date, dateFormat))

      if (selectedDays.length > 0) {
        daysByRow[entity.id] = selectedDays
      }
    }
  } else if (selection.kind === 'column') {
    const slot = gridSlots[selection.columnIndex]
    if (!slot) return daysByRow
    const date = moment(slot.start)
    const dateStr = i18nDateToString(date, dateFormat)
    for (const entity of entities) {
      if (entity.id === TIME_OFF_ENTITY_ID || entity.selectable === false) continue
      if (!isSelectableDayForGridSelection(entity.id, date)) continue
      daysByRow[entity.id] = [dateStr]
    }
  } else if (selection.kind === 'columnRange') {
    const selectedSlots = gridSlots.filter((slot) => selection.keys.has(slot.index))
    if (selectedSlots.length === 0) return daysByRow

    for (const entity of entities) {
      if (entity.id === TIME_OFF_ENTITY_ID || entity.selectable === false) continue

      const selectedDays = selectedSlots
        .map((slot) => moment(slot.start))
        .filter((date) => isSelectableDayForGridSelection(entity.id, date))
        .map((date) => i18nDateToString(date, dateFormat))

      if (selectedDays.length > 0) {
        daysByRow[entity.id] = selectedDays
      }
    }
  }

  return daysByRow
}

const collectTargetsForEventSelection = (
  selectionKeys: Set<string>,
  events: ExtendedGridEventAppearance[]
): {
  shifts: Record<string, Shift>
  restDays: Record<string, RestDay>
} => {
  const shifts: Record<string, Shift> = {}
  const restDays: Record<string, RestDay> = {}
  for (const event of events) {
    if (!selectionKeys.has(`${event.entityId}:${event.id}`)) continue
    if (event.type === 'shift') {
      const shiftEvent: ShiftGridEvent = event
      const selectionDate = moment(shiftEvent.start)
      shifts[shiftEvent.shift.referenceKey(selectionDate)] = shiftEvent.shift
      continue
    }
    if (event.type === 'rest_day') {
      const restDayEvent: RestDayGridEvent = event
      restDays[restDayEvent.restDay.id] = restDayEvent.restDay
    }
  }
  return { shifts, restDays }
}

const collectShiftsForSelection = (
  daysByRow: DaysByRow,
  events: ExtendedGridEventAppearance[],
  dateFormat: FormatTypes
): Record<string, Shift> => {
  const shifts: Record<string, Shift> = {}
  for (const event of events) {
    if (event.type !== 'shift') continue
    const shiftEvent: ShiftGridEvent = event
    const selectedDays = daysByRow[shiftEvent.entityId]
    if (!selectedDays) continue
    const date = moment(shiftEvent.start)
    const dateStr = i18nDateToString(date, dateFormat)
    if (!selectedDays.includes(dateStr)) continue
    const shift = shiftEvent.shift
    shifts[shift.referenceKey(date)] = shift
  }
  return shifts
}

const collectRestDaysForSelection = (
  daysByRow: DaysByRow,
  events: ExtendedGridEventAppearance[],
  dateFormat: FormatTypes
): Record<string, RestDay> => {
  const restDays: Record<string, RestDay> = {}
  for (const event of events) {
    if (event.type !== 'rest_day') continue
    const restDayEvent: RestDayGridEvent = event
    const selectedDays = daysByRow[restDayEvent.entityId]
    if (!selectedDays) continue
    const date = moment(restDayEvent.start)
    const dateStr = i18nDateToString(date, dateFormat)
    if (!selectedDays.includes(dateStr)) continue
    const restDay = restDayEvent.restDay
    restDays[restDay.id] = restDay
  }
  return restDays
}

// Number of elements preloaded before and after the visible area
const BASIC_VERSION_OFFSET = 5
const FULL_VERSION_OFFSET = 10
const TIME_OFF_ENTITY_ID = '__time_off__'
interface Props {
  view: View
  shiftsRows: TShiftsRow[]
  balancesByEmployee: Record<string, TimePlanningBalance>
  periodBalancesByEmployee: Record<string, TimePlanningBalancesByPeriod[]>
}

type TimePlanningBalance = NonNullable<
  PlanningBalancesQuery['timePlanning']['balancesConnection']['nodes']
>[number]
type TimePlanningBalancesByPeriod = NonNullable<
  PlanningBalancesByPeriodsQuery['timePlanning']['balancesByPeriodsConnection']['nodes']
>[number]

const ShiftManagementTimeGrid = ({
  view,
  shiftsRows,
  balancesByEmployee,
  periodBalancesByEmployee,
}: Props) => {
  const [{ shiftsConflicts }] = useSchedule()
  const [isTimeOffSidePanelOpen, setIsTimeOffSidePanelOpen] = useState(false)
  const [selectedTimeOffDate, setSelectedTimeOffDate] = useState<moment.Moment>(moment())
  const granularity: TimeGranularity = view.granularity
  const rowHeaderWidth = resolveDimension('s240', 240)
  const minColumnWidth = resolveDimension(view.columnMinWidth, 80)
  const rowMinHeight = resolveDimension(view.cellMinHeight, 48)
  const shouldShowTimeOffRow = view.isByAreas
  const [localStorageResizeHeight] = useLocalStorage<number>(
    LOCAL_STORAGE_KEYS.shiftManagementCoverageResizeHeight,
    0
  )
  const timeRange = useUnsafeMemo(
    () => ({ start: view.firstDay.toDate(), end: view.lastDay.toDate() }),
    [view.firstDay, view.lastDay]
  )

  const preloadedRowsOutsideViewport = useUnsafeMemo(() => {
    return view.isFullVersion ? FULL_VERSION_OFFSET : BASIC_VERSION_OFFSET
  }, [view.isFullVersion])

  const {
    leaves,
    restDays,
    holidays,
    loading: isTimeOffLoading,
  } = useGetTimeOffRow({
    skip: !shouldShowTimeOffRow,
  })

  const slots = useUnsafeMemo(() => {
    if (!shouldShowTimeOffRow) return []
    return buildTimeSlots({ timeRange, granularity }).slots
  }, [granularity, shouldShowTimeOffRow, timeRange])

  const timeOffSummaryData = useUnsafeMemo(
    () => ({
      holidays,
      leaves,
      restDays,
      loading: isTimeOffLoading,
    }),
    [holidays, leaves, restDays, isTimeOffLoading]
  )

  const handleOpenTimeOffSidepanel = useUnsafeCallback((date: moment.Moment) => {
    setSelectedTimeOffDate(date)
    setIsTimeOffSidePanelOpen(true)
  }, [])

  const timeOffSummary = useBuildTimeOffSummary({
    shouldShow: shouldShowTimeOffRow,
    slots,
    entityId: TIME_OFF_ENTITY_ID,
    data: timeOffSummaryData,
    onOpenSidepanel: handleOpenTimeOffSidepanel,
  })

  const events = useBuildEvents({
    shiftsRows,
    view,
    shiftsConflicts,
    timeOffSummary,
  })

  const interactiveSlots = useUnsafeMemo(() => {
    const extraHours = computeExtraHoursForTimeRange(events, timeRange, granularity)

    return buildTimeSlots({ timeRange, granularity, extraHours }).slots
  }, [events, granularity, timeRange])

  const shiftsRowsByKey = useUnsafeMemo(
    () => new Map(shiftsRows.map((row) => [row.key, row])),
    [shiftsRows]
  )

  const entities = useUnsafeMemo(() => {
    const baseEntities = shiftsRows.map((row) => {
      if (!view.isByEmployees) return { id: row.key, selectable: true }
      return { id: row.key, selectable: row.canManage }
    })
    if (!shouldShowTimeOffRow) return baseEntities

    return [{ id: TIME_OFF_ENTITY_ID, selectable: false }, ...baseEntities]
  }, [shouldShowTimeOffRow, shiftsRows, view.isByEmployees])

  const shouldEnableClipboardInTable = !view.isByAreas
  const clipboardHandlers = useGridClipboardActions({
    canUseClipboardActions: true,
    shiftsRows,
  })
  const { deleteModal, handleDeleteShortcut } = useDeleteSelectionFlow({})
  const { moveShift } = useMoveShift()

  const handleDropUnassignedShift = useUnsafeCallback(
    async ({
      unassignedShift,
      targetEntityId,
      targetDate,
    }: {
      unassignedShift: UnassignedShift
      targetEntityId: string
      targetDate: moment.Moment
    }) => {
      const targetRow = shiftsRowsByKey.get(targetEntityId)
      if (!targetRow) return

      const employeeId = targetRow.id
      const formattedDate = i18nDateToString(targetDate, 'backend')
      const daysByEmployee: Record<string, string[]> = {
        [String(employeeId)]: [formattedDate],
      }

      await moveShift({
        newShiftsRow: targetRow,
        unassignedShift,
        date: targetDate,
        daysByEmployee,
        employeeId,
      })
    },
    [moveShift, shiftsRowsByKey]
  )

  const dragAndDropAdapter = useGridDragAndDropAdapter({
    clipboardHandlers,
    view,
    onDropUnassignedShift: handleDropUnassignedShift,
  })

  const [{ canUseCoverageView }] = useEnabledFeatures()
  const [{ canManageUnassignedShifts }] = usePermissions()
  const canViewCoverageSection =
    canUseCoverageView && canManageUnassignedShifts && view.isByEmployees

  const [hover, setHover] = useState(false)
  const [isQuickActionsOpen, setIsQuickActionsOpen] = useState(false)
  const [quickActionsAnchorRect, setQuickActionsAnchorRect] = useState<DOMRect | null>(null)
  const [ghostAnchorRect, setGhostAnchorRect] = useState<DOMRect | null>(null)
  const [quickActionsSelection, setQuickActionsSelection] = useState<GhostCell | null>(null)
  const [quickActionsGhostCells, setQuickActionsGhostCells] = useState<GhostCell[]>([])

  const resizeRef = useRef<HTMLDivElement>(null)
  const coverageColumns = useCoverageColumns(view)
  const { defaultShifts: visibleDefaultShifts, loading: isDefaultShiftsLoading } =
    useFetchDefaultShifts({})

  const dispatchSelection = useSelectionDispatch()
  const selectionState = useSelectionState()
  const [{ hasMore }] = usePagination()
  const [filters] = useFilters()
  const { localStorageOrderEmployeesBy, localStorageOrderEmployeesDir } = useOrderOptions()
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const hasLegacyJobCatalogFeature = hasFeatureFlag('legacy_job_catalog')
  const employeeIds = useUnsafeMemo(() => {
    if (!filters.terminated) return filters.employeeIds

    const requestedIds = filters.employeeIds
    return requestedIds && requestedIds.length > 0 ? requestedIds : null
  }, [filters.employeeIds, filters.terminated])
  const [fetchSelectableEmployees] = useLazyQuery(ShiftManagementSelectableShiftsRowsDocument)

  const isDisabledDay = useUnsafeCallback(
    (rowKey: string, date: moment.Moment) => {
      const row = shiftsRowsByKey.get(rowKey)
      if (!row) return false

      return row.isDisabled(view, date)
    },
    [shiftsRowsByKey, view]
  )

  const isSelectableDayForGridSelection = useUnsafeCallback(
    (rowKey: string, date: moment.Moment) => {
      return !isDisabledDay(rowKey, date)
    },
    [isDisabledDay]
  )

  const isCellSelectable = useUnsafeCallback(
    (entity: { id: string }, slot: TimeSlot) => {
      if (entity.id === TIME_OFF_ENTITY_ID) return false

      return !isDisabledDay(entity.id, moment(slot.start))
    },
    [isDisabledDay]
  )

  const isCellDisabled = useUnsafeCallback(
    (entity: { id: string }, slot: TimeSlot) => {
      if (entity.id === TIME_OFF_ENTITY_ID) return false

      return isDisabledDay(entity.id, moment(slot.start))
    },
    [isDisabledDay]
  )

  // Single-event selection is card-local only (opens the card actions menu)
  // and intentionally does NOT sync to global/header selection. Multi-event
  // selections (Cmd/Ctrl+click) DO sync so that bulk actions work from the top bar.
  const isSingleEventCardSelection = (selection: GridSelection): boolean => {
    if (selection.kind !== 'event') return false
    return (
      (selection.kind === 'event' &&
        selection.keys.size === 1 &&
        selection.keys.values().next().value?.includes('shift')) ||
      false
    )
  }

  const fetchAllSelectableEntities = useUnsafeCallback(async () => {
    dispatchSelection({ type: 'RESOLVE_GRID_SELECTION', isResolvingGridSelection: true })

    try {
      const { data } = await fetchSelectableEmployees({
        variables: {
          contractOnDate: view.startAt,
          orderBy: localStorageOrderEmployeesBy,
          orderDir: localStorageOrderEmployeesDir,
          employeeIds,
          endAt: view.endAt,
          endAtISO: view.endAt,
          logicOperator: LOGIC_OPERATOR.AND,
          shiftIds: filters.shiftIds,
          withoutShiftIds: filters.withoutShiftIds,
          startAt: view.startAt,
          startAtISO: view.startAt,
          terminated: filters.terminated,
          locationIds: filters.locationIds,
          teamIds: filters.teamIds,
          legalEntityIds: filters.legalEntityIds,
        },
      })

      return (
        data?.shiftManagement?.employeesPermissions.map((row: TShiftsRowGraphQL) =>
          ShiftsRow.buildFromGraphQL(row, ShiftsRowType.employee, hasLegacyJobCatalogFeature)
        ) || []
      ).map((row) => ({ id: row.key, selectable: row.canManage }))
    } finally {
      dispatchSelection({ type: 'RESOLVE_GRID_SELECTION', isResolvingGridSelection: false })
    }
  }, [
    fetchSelectableEmployees,
    employeeIds,
    filters.legalEntityIds,
    filters.locationIds,
    filters.shiftIds,
    filters.teamIds,
    filters.terminated,
    filters.withoutShiftIds,
    hasLegacyJobCatalogFeature,
    localStorageOrderEmployeesBy,
    localStorageOrderEmployeesDir,
    view.endAt,
    view.startAt,
  ])

  const handleSelectionChange = useUnsafeCallback(
    async (selection: GridSelection, selectionSlots: TimeSlot[]) => {
      if (selection.kind === 'none') {
        dispatchSelection({ type: 'CLEAR_SELECTION' })
        return
      }
      if (isSingleEventCardSelection(selection)) {
        dispatchSelection({ type: 'CLEAR_SELECTION' })
        return
      }

      const selectionEntities =
        view.isByEmployees &&
        hasMore &&
        (selection.kind === 'all' ||
          selection.kind === 'column' ||
          selection.kind === 'columnRange')
          ? await fetchAllSelectableEntities()
          : entities

      const daysByRow = buildDaysByRow(selection, selectionSlots, {
        events,
        entities: selectionEntities,
        dateFormat: view.dateFormat,
        isSelectableDayForGridSelection,
      })

      if (Object.keys(daysByRow).length === 0) {
        dispatchSelection({ type: 'CLEAR_SELECTION' })
        return
      }

      const { shifts: selectedShifts, restDays: selectedRestDays } =
        selection.kind === 'event'
          ? collectTargetsForEventSelection(selection.keys, events)
          : {
              shifts: collectShiftsForSelection(daysByRow, events, view.dateFormat),
              restDays: collectRestDaysForSelection(daysByRow, events, view.dateFormat),
            }
      dispatchSelection({
        type: 'SET_FROM_GRID',
        daysByRow,
        shifts: selectedShifts,
        restDays: selectedRestDays,
        kind: selection.kind,
      })
    },
    [
      dispatchSelection,
      entities,
      events,
      fetchAllSelectableEntities,
      hasMore,
      isSelectableDayForGridSelection,
      view.isByEmployees,
      view.dateFormat,
    ]
  )

  const handleAddShiftClick = useUnsafeCallback(
    (entity: { id: string }, slotIndex: number, anchorRect: DOMRect) => {
      const slot = interactiveSlots[slotIndex]
      const targetRow = shiftsRowsByKey.get(entity.id)
      if (!slot || !targetRow) return
      if (!isCellSelectable(entity, slot) || isCellDisabled(entity, slot)) return

      const targetDate = moment(slot.start)
      const targetDateStr = i18nDateToString(targetDate, view.dateFormat)
      const currentDaysForRow = selectionState.daysByRow[entity.id] ?? []
      const targetBelongsToCurrentSelection = currentDaysForRow.includes(targetDateStr)
      const slotIndexByDate = new Map(
        interactiveSlots.map((timeSlot, index) => [
          i18nDateToString(moment(timeSlot.start), view.dateFormat),
          index,
        ])
      )
      const nextDaysByRow = targetBelongsToCurrentSelection
        ? selectionState.daysByRow
        : { [entity.id]: [targetDateStr] }
      const nextGhostCells = Object.entries(nextDaysByRow).flatMap(([entityId, dates]) => {
        return dates.flatMap((dateStr) => {
          const matchingSlotIndex = slotIndexByDate.get(dateStr)

          return matchingSlotIndex === undefined ? [] : [{ entityId, slotIndex: matchingSlotIndex }]
        })
      })

      if (!targetBelongsToCurrentSelection) {
        dispatchSelection({ type: 'CLEAR_SELECTION' })
      }

      setIsQuickActionsOpen(true)
      setQuickActionsSelection({ entityId: entity.id, slotIndex })
      setQuickActionsGhostCells(nextGhostCells)
      setQuickActionsAnchorRect(anchorRect)
      setGhostAnchorRect(null)
      dispatchSelection({ type: 'SHOW_EMPTY_SHIFT_CELL' })

      if (!targetBelongsToCurrentSelection) {
        dispatchSelection({
          type: 'TOGGLE_DAY',
          day: targetDate,
          shiftsRow: targetRow,
          view,
          forcedValue: true,
        })
      }

      const impactedEmployees = Object.keys(nextDaysByRow).length || 1
      const cellCount =
        Object.values(nextDaysByRow).reduce((sum, dates) => sum + dates.length, 0) || 1

      trackAddShiftStartEvent({
        view,
        impactedEmployees,
        cellCount,
        shiftType: 'savedCustom',
        from: 'contextual_button',
        quickActions: true,
      })
    },
    [
      dispatchSelection,
      interactiveSlots,
      isCellDisabled,
      isCellSelectable,
      selectionState.daysByRow,
      shiftsRowsByKey,
      view,
    ]
  )

  const selectedShiftRow = useUnsafeMemo(() => {
    if (!quickActionsSelection) return null
    return shiftsRowsByKey.get(quickActionsSelection.entityId) ?? null
  }, [quickActionsSelection, shiftsRowsByKey])

  const selectedDate = useUnsafeMemo(() => {
    if (!quickActionsSelection) return null
    const slot = interactiveSlots[quickActionsSelection.slotIndex]
    if (!slot) return null
    return moment(slot.start)
  }, [interactiveSlots, quickActionsSelection])

  const closeQuickActions = useUnsafeCallback(() => {
    setIsQuickActionsOpen(false)
    setQuickActionsSelection(null)
    setQuickActionsGhostCells([])
    setQuickActionsAnchorRect(null)
    setGhostAnchorRect(null)
    dispatchSelection({ type: 'HIDE_EMPTY_SHIFT_CELL' })
    dispatchSelection({ type: 'CLEAR_SELECTION' })
  }, [dispatchSelection])

  const handleGhostCardRef = useUnsafeCallback(
    (node: HTMLDivElement | null) => {
      if (!isQuickActionsOpen || !node) return
      const nextRect = node.getBoundingClientRect()

      setGhostAnchorRect((prevRect) => {
        if (!prevRect) return nextRect

        const samePosition =
          Math.abs(prevRect.left - nextRect.left) < 0.5 &&
          Math.abs(prevRect.top - nextRect.top) < 0.5 &&
          Math.abs(prevRect.width - nextRect.width) < 0.5 &&
          Math.abs(prevRect.height - nextRect.height) < 0.5

        return samePosition ? prevRect : nextRect
      })
    },
    [isQuickActionsOpen]
  )

  const forcedQuickActionsAnchorRect = useUnsafeMemo(() => {
    if (!isQuickActionsOpen || !quickActionsSelection) return null
    return ghostAnchorRect ?? quickActionsAnchorRect
  }, [ghostAnchorRect, isQuickActionsOpen, quickActionsAnchorRect, quickActionsSelection])
  return (
    <>
      <TimeGridTable
        entities={entities}
        events={events}
        timeRange={timeRange}
        granularity={granularity}
        rowHeaderLabel={view.isByAreas ? <SortWorkAreasButton /> : <SortEmployeesButton />}
        rowHeaderWidth={rowHeaderWidth}
        minColumnWidth={minColumnWidth}
        rowMinHeight={rowMinHeight}
        paddingX={4}
        paddingY={4}
        cardLayout={{
          minHeight: rowMinHeight,
          paddingX: 6,
          paddingY: 4,
          radius: 8,
        }}
        preloadedRowsOutsideViewport={preloadedRowsOutsideViewport}
        timeSlotFormatter={(slot) => formatHeader(slot.start, view, view.isTimeFormat24H)}
        isCellSelectable={isCellSelectable}
        isCellDisabled={isCellDisabled}
        rowHeaderRenderer={(entity) => {
          if (entity.id === TIME_OFF_ENTITY_ID) {
            return (
              <Box width="full" justifyContent="center" paddingTop="s4" paddingX="s16">
                <F0Text
                  variant="label"
                  content={i18n.t('shift_management.time_off_row.title')}
                  ellipsis
                />
              </Box>
            )
          }

          const row = shiftsRowsByKey.get(entity.id)
          if (!row) return null

          return (
            <FirstCell
              view={view}
              shiftsRow={row}
              balances={balancesByEmployee[row.key]}
              periodBalances={periodBalancesByEmployee[row.key]}
            />
          )
        }}
        cardAppearance={(event) => resolveCard(event.type).getAppearance(event)}
        eventRenderer={(event, context) => resolveCard(event.type).render(event, context)}
        dragAndDropAdapter={dragAndDropAdapter}
        clipboardHandlers={shouldEnableClipboardInTable ? clipboardHandlers : undefined}
        onDeleteShortcut={handleDeleteShortcut}
        onSelectionChange={handleSelectionChange}
        onAddShiftClick={handleAddShiftClick}
        ghostCells={isQuickActionsOpen ? quickActionsGhostCells : null}
        ghostAnchorCell={isQuickActionsOpen ? quickActionsSelection : null}
        onGhostCardRef={handleGhostCardRef}
        keyboardScrollMargin={{
          top: HEADER_OFFSET,
          bottom:
            localStorageResizeHeight === 0
              ? COVERAGE_SECTION_HEADER
              : localStorageResizeHeight + COVERAGE_SECTION_HEADER,
        }}
      >
        {canViewCoverageSection && (
          <Box
            justifyContent="flexEnd"
            width="full"
            style={{ position: 'sticky', bottom: '0', zIndex: '1', marginTop: 'auto' }}
          >
            <Box className="bg-f1-background" ref={resizeRef}>
              <CoverageSection
                columns={coverageColumns}
                view={view}
                hover={hover}
                setHover={setHover}
                resizeRef={resizeRef}
              />
            </Box>
          </Box>
        )}
      </TimeGridTable>
      {isQuickActionsOpen &&
        quickActionsSelection &&
        forcedQuickActionsAnchorRect &&
        selectedShiftRow &&
        selectedDate && (
          <QuickActionsPopover
            date={selectedDate}
            shiftsRow={selectedShiftRow}
            visibleDefaultShifts={visibleDefaultShifts}
            isDefaultShiftsLoading={isDefaultShiftsLoading}
            forceOpen
            forceAnchorRect={forcedQuickActionsAnchorRect}
            onCloseForced={closeQuickActions}
          />
        )}
      {isTimeOffSidePanelOpen ? (
        <TimeOffSidepanel
          open={isTimeOffSidePanelOpen}
          onClose={() => setIsTimeOffSidePanelOpen(false)}
          selectedDate={selectedTimeOffDate}
          leaves={leaves}
          restDays={restDays}
          holidays={holidays}
        />
      ) : null}
      {deleteModal}
    </>
  )
}

export { ShiftManagementTimeGrid }
